import { useState } from 'react'
import { batch, useDispatch, useSelector } from 'react-redux'
import {
  clearSelected,
  fetchProposalKeywords,
  reset,
  selectAll,
  setKeyword,
  setSelected,
} from 'features/keywordPlanner/proposalKeywordSlice'
import Button from 'components/form/Button'
import Icon from 'components/Icon'
import SearchInput from 'components/form/SearchInput'
import Table from 'components/table/Table'
import TableCheckBox from 'components/table/TableCheckBox'
import HelpTooltip from 'components/tooltip/HelpTooltip'
import ProposalKeywordItem from 'features/keywordPlanner/ProposalKeywordItem'
import { downloadCsvFile } from 'utils'
import {
  setKeywords,
  setVisible,
} from 'features/keywordPlanner/predictKeywordSlice'
import { useDialog } from 'features/dialog/Dialog'
import { fail, failWithErrorResponse } from 'features/toast/Toast'
import AddKeywordsModal from 'features/keywordPlanner/AddKeywordsModal'

const ProposalKeyword = () => {
  const dispatch = useDispatch()
  const { isLoading, content, keyword, selected } = useSelector(
    state => state.keywordPlanner.proposalKeyword
  )
  const [searchText, setSearchText] = useState(keyword)
  const [CopyKeywords, showCopyKeywords, hideCopyKeywords] = useDialog()
  const allChecked =
    selected.length > 0 &&
    content.every(item => selected.includes(item.keyword))

  const handleKeywordChange = ({ target: { value } }) => {
    setSearchText(value)
  }

  const handleSearch = () => {
    batch(async () => {
      dispatch(setKeyword(searchText))
      dispatch(clearSelected())
      const { error, payload } = await dispatch(fetchProposalKeywords())
      if (error) failWithErrorResponse(payload)
    })
  }

  const handleReset = () => {
    setSearchText('')
    dispatch(reset())
  }

  const handleRefresh = () => {
    dispatch(fetchProposalKeywords())
  }

  const handleChackAll = checked => {
    if (checked) {
      dispatch(selectAll())
    } else {
      dispatch(clearSelected())
    }
  }

  const handleDownload = () => {
    if (content.length === 0) {
      fail('다운로드할 데이터가 없습니다.')
    } else {
      const defaultStat = {
        bidRequest: '',
        click: '',
        ctr: '',
        averageBid: '',
        maxBidPrice: '',
      }
      const column = [
        '키워드',
        '모바일 광고요청수',
        '모바일 클릭수',
        '모바일 클릭률',
        '모바일 평균경쟁광고수',
        '모바일 최고입찰가',
        'PC 광고요청수',
        'PC 클릭수',
        'PC 클릭률',
        'PC 평균경쟁광고수',
        'PC 최고입찰가',
      ].join(',')
      const row = content
        .map(({ keyword: kwd, stats }) => {
          const mobile =
            stats?.find(item => item.adunitDevice === 'MOBILE') || defaultStat
          const pc =
            stats?.find(item => item.adunitDevice === 'PC') || defaultStat
          return [
            kwd,
            mobile.bidRequest,
            mobile.click,
            mobile.ctr,
            mobile.averageBid,
            mobile.maxBidPrice,
            pc.bidRequest,
            pc.click,
            pc.ctr,
            pc.averageBid,
            pc.maxBidPrice,
          ].join(',')
        })
        .join('\n')
      const fileName = `키워드플래너_${keyword}.csv`
      downloadCsvFile(`${column}\n${row}`, fileName)
    }
  }

  const showPredictKeywords = () => {
    batch(() => {
      dispatch(setKeywords(selected))
      dispatch(setVisible(true))
    })
  }

  const handleDeselect = v => {
    dispatch(setSelected(v))
  }

  return (
    <>
      <div className="ad_managebox">
        <div className="managebox_tit">
          <strong className="tit_box">키워드 리스트</strong>
          <div className="f_right">
            <Button line icon disabled={isLoading} onClick={handleDownload}>
              <Icon name="download">다운로드</Icon>
            </Button>
          </div>
        </div>
        <div className="managebox_tit">
          <div className="f_left">
            <div className="single_wrap">
              <div className="search_box">
                <SearchInput
                  placeholder="키워드를 입력하세요"
                  value={searchText}
                  onSearch={handleSearch}
                  onChange={handleKeywordChange}
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="single_wrap">
              <Button line icon disabled={isLoading} onClick={handleReset}>
                <Icon name="refresh">새로고침</Icon>
              </Button>
            </div>
          </div>
          <div className="f_right">
            <div className="single_wrap">
              <Button
                primary
                disabled={selected.length === 0}
                onClick={showPredictKeywords}>
                예상실적
              </Button>
            </div>
            <div className="single_wrap">
              <Button
                line
                onClick={showCopyKeywords}
                disabled={selected.length === 0}>
                키워드 바로등록
              </Button>
            </div>
          </div>
        </div>
        <div className="tblg2_wrap">
          <div className="item_head">
            <Table className="tbl_g2" caption="키워드플래너 상단안내 표">
              <colgroup>
                <col width="57px" />
                <col width="245px" />
                <col width="82px" />
                <col width="82px" />
                <col width="82px" />
                <col width="104px" />
                <col width="88px" />
                <col width="82px" />
                <col width="82px" />
                <col width="82px" />
                <col width="104px" />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th rowSpan="2" scope="col" className="align_c">
                    <div className="inner_tbl">
                      <TableCheckBox
                        checked={allChecked}
                        onCheckedChange={handleChackAll}
                      />
                    </div>
                  </th>
                  <th rowSpan="2" scope="col">
                    <div className="inner_tbl">키워드</div>
                  </th>
                  <th colSpan="5" scope="colgroup" className="align_c">
                    <div className="inner_tbl">
                      모바일
                      <HelpTooltip>
                        매체유형이 검색매체 &gt; 카카오이며, 모바일에서 발생된
                        최근30일간의 데이터 기준입니다.
                      </HelpTooltip>
                    </div>
                  </th>
                  <th colSpan="5" scope="colgroup" className="align_c">
                    <div className="inner_tbl">
                      PC
                      <HelpTooltip>
                        매체유형이 검색매체 &gt; 카카오이며, PC에서 발생된
                        최근30일간의 데이터 기준입니다.
                      </HelpTooltip>
                    </div>
                  </th>
                </tr>
                <tr>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">
                      광고요청수
                      <HelpTooltip>
                        광고가 노출될 지면에서 광고를 요청하는 수로 검색쿼리수와
                        유사합니다.
                      </HelpTooltip>
                    </div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">클릭수</div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">
                      클릭률
                      <HelpTooltip>
                        클릭수를 광고요청수로 나눈 값을 의미합니다.
                        <br />
                        (클릭률(%) = 클릭수/광고요청수*100%)
                      </HelpTooltip>
                    </div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">평균경쟁광고수</div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">최고입찰가</div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">
                      광고요청수
                      <HelpTooltip>
                        광고가 노출될 지면에서 광고를 요청하는 수로 검색쿼리수와
                        유사합니다.
                      </HelpTooltip>
                    </div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">클릭수</div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">
                      클릭률
                      <HelpTooltip>
                        클릭수를 광고요청수로 나눈 값을 의미합니다.
                        <br />
                        (클릭률(%) = 클릭수/광고요청수*100%)
                      </HelpTooltip>
                    </div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">평균경쟁광고수</div>
                  </th>
                  <th scope="col" className="align_c">
                    <div className="inner_tbl">최고입찰가</div>
                  </th>
                </tr>
              </thead>
            </Table>
          </div>
          <div className="item_body">
            <Table
              className="tbl_g2"
              caption="키워드플래너 전체 표"
              loading={isLoading}
              emptyMessage={
                <>
                  조회결과가 없습니다.
                  <br />
                  상단에서 키워드를 조회하세요.
                </>
              }>
              <Table.Head noHead>
                <Table.Column width={57} />
                <Table.Column width={245} />
                <Table.Column width={82} />
                <Table.Column width={82} />
                <Table.Column width={82} />
                <Table.Column width={104} />
                <Table.Column width={88} />
                <Table.Column width={82} />
                <Table.Column width={82} />
                <Table.Column width={82} />
                <Table.Column width={104} />
                <Table.Column />
              </Table.Head>
              <Table.Body>
                {content.map(data => (
                  <ProposalKeywordItem key={data.keyword} data={data} />
                ))}
              </Table.Body>
            </Table>
          </div>
        </div>
        <div className="item_info">
          <ul className="list_dot">
            <li>
              제안된 키워드는 심사승인을 보장하지 않으며, 키워드 사용에 따른
              모든 법적 책임은 광고주에게 있습니다.
            </li>
            <li>
              제공되는 데이터는 프리미엄링크 캠페인 &gt; 검색매체 &gt;
              카카오지면을 통해 광고가 집행된 키워드의 최근30일(어제로부터 과거
              30일까지) 과거 실적 및 과거 실적을 토대로 예측한 수치입니다.
              <br />
              따라서, 제공되는 데이터로 인한 광고 성과를 보장하지 않으니,
              참고용으로만 사용하시기 바랍니다.
            </li>
          </ul>
        </div>
      </div>
      <CopyKeywords type="basic_layer" className="copy_layer">
        <AddKeywordsModal
          selectedKeywords={selected}
          handleClose={hideCopyKeywords}
          handleDeselect={handleDeselect}
          afterConfirm={handleRefresh}
        />
      </CopyKeywords>
    </>
  )
}
export default ProposalKeyword
